// Story 11.10 (ADO-1961), the second PR: what the margin info panel says (AC2, OQ-3; the
// designer's 3 Nov export, frame M4). The designer's copy is used verbatim, glyphs included
// ("—", "−", "÷").
//
// The PDP panel fills in the product's own figures (MI-8): the quote's RSP, the product's VAT rate
// as the PDP's VAT row shows it, the RSP without VAT worked out here exactly, the quote's case
// price (without the deposit, the price the margin uses) and the quote's margin as the PDP shows
// it. Units per case are not on the web, so the per-unit step is described in words. The order
// review's panel is the formula alone: its margin is per line.

import type { InfoPanelContent } from '../design-system';
import { formatMoney, type MoneyValue } from '../shared/money';
import { formatVatRate } from '../shared/vatRate';
import type { PriceEntry } from './usePriceStore';

type PricedEntry = Extract<PriceEntry, { state: 'priced' }>;

export const MARGIN_INFO_TITLE = 'How margin is worked out';
export const MARGIN_INFO_DEFINITION = 'Your profit as a % of the shelf price without VAT.';
export const MARGIN_INFO_NOTE =
  'Your price is what you pay on this line: the promotion or contract price if one applies, without the deposit.';
const NO_VAT_RATE_BODY =
  'We can\'t work out margin for this product because its VAT rate is missing, so it shows as "—".';
const PDP_FORMULA = 'Margin = (RSP without VAT − price per unit) ÷ RSP without VAT';
const PROMOTION_SUFFIX = ' (with this promotion)';

/** The order review's MARGIN header panel (frame M4): the formula and the note, no result. */
export const ORDER_REVIEW_MARGIN_INFO: InfoPanelContent = {
  title: MARGIN_INFO_TITLE,
  definition: MARGIN_INFO_DEFINITION,
  steps: [
    'Margin = (RSP without VAT − your price per unit) ÷ RSP without VAT',
    'Shown for each line.',
  ],
  note: MARGIN_INFO_NOTE,
};

/** A VAT multiplier as written on the wire (`'1.23'`, `'1.135'`, `'1.00'`). */
const MULTIPLIER_TEXT = /^(\d+)(?:\.(\d+))?$/;

/**
 * The RSP without VAT, `rrp ÷ multiplier`, in exact decimal arithmetic and rounded once to cents
 * half away from zero (MI-8), or `null` when the multiplier is not a positive decimal or the RSP
 * is not above zero. The RSP is whole cents (the quote's own), so it is taken in cents; the
 * multiplier is taken as an integer over a power of ten, never as a float.
 */
export function rrpWithoutVat(rrp: MoneyValue, multiplier: string): MoneyValue | null {
  const match = MULTIPLIER_TEXT.exec(multiplier);
  if (match === null || !(rrp.amount > 0)) return null;
  const fraction = match[2] ?? '';
  const multiplierScaled = BigInt(`${match[1]}${fraction}`);
  if (multiplierScaled === 0n) return null;
  const scale = 10n ** BigInt(fraction.length);
  const rrpCents = BigInt(Math.round(rrp.amount * 100));
  // cents = rrpCents × scale ÷ multiplierScaled, rounded half up (both are positive).
  const numerator = rrpCents * scale;
  const cents = (2n * numerator + multiplierScaled) / (2n * multiplierScaled);
  return { amount: Number(cents) / 100, currency: rrp.currency };
}

/**
 * The PDP's margin info panel for a priced quote, or `null` when it shows no button (MI-3): the
 * figures panel for an available margin, the no-VAT panel for a margin unavailable for want of a
 * VAT rate, and nothing for any other cause, a margin with no reason or no margin at all. The
 * figures panel also needs the RSP and a VAT rate the PDP can show; without either it is `null`
 * rather than a panel with a gap (MI-10). `vatRate` is the product detail's multiplier.
 */
export function pdpMarginInfo(entry: PricedEntry, vatRate: string | null): InfoPanelContent | null {
  const margin = entry.margin;
  if (margin === undefined) return null;
  if (margin.status === 'unavailable') {
    return margin.reason === 'NO_VAT_RATE'
      ? { title: MARGIN_INFO_TITLE, definition: MARGIN_INFO_DEFINITION, steps: [NO_VAT_RATE_BODY] }
      : null;
  }
  const vatPercent = formatVatRate(vatRate);
  if (entry.rrp === undefined || vatRate === null || vatPercent === null) return null;
  const withoutVat = rrpWithoutVat(entry.rrp, vatRate);
  if (withoutVat === null) return null;
  return {
    title: MARGIN_INFO_TITLE,
    definition: MARGIN_INFO_DEFINITION,
    steps: [
      `RSP ${formatMoney(entry.rrp)} incl. ${vatPercent} VAT = ${formatMoney(withoutVat)} without VAT`,
      `Your price per unit = case price ${formatMoney(entry.unitPrice)} ÷ units in the case`,
      PDP_FORMULA,
    ],
    result: `Margin ${margin.percent}%${entry.kind === 'promotion' ? PROMOTION_SUFFIX : ''}`,
    note: MARGIN_INFO_NOTE,
  };
}
